import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RegisterForm, LoginCard, EmailAuth, useAuth } from '@/components/auth';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Key, Mail, AlertCircle } from 'lucide-react';
import logo from '../logo.svg';

export function LoginPage() {
  const { login } = useAuth();
  const [mode, setMode] = useState<'login' | 'register' | 'email'>('login');
  const [urlToken, setUrlToken] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);

  // Check for URL parameters on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const error = urlParams.get('error');

    if (token) {
      setUrlToken(token);
      setMode('email');
    }

    if (error) {
      switch (error) {
        case 'missing_token':
          setUrlError('Invalid magic link. Please request a new one.');
          break;
        default:
          setUrlError('Authentication error. Please try again.');
      }
    }

    // Clean up URL parameters
    if (token || error) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleAuthSuccess = (user: { id: string; username: string; isAdmin?: boolean }, token: string) => {
    login(user, token);
  };

  const handleAuthError = (error: string) => {
    console.error('Authentication error:', error);
  };

  return (
    <>
      <div className="flex justify-center items-center gap-8 mb-8">
        <img
          src={logo}
          alt="LAION Logo"
          className="h-36 p-6 transition-all duration-300 hover:drop-shadow-[0_0_2em_#646cffaa] scale-120"
        />
      </div>

      <Card className="bg-card/50 backdrop-blur-sm border-muted mb-8">
        <CardContent className="pt-6">
          <h1 className="text-5xl font-bold my-4 leading-tight">LAION Projects</h1>
          <p className="mb-4">
            Help us annotate the data for open research!
          </p>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>✓ Help us democratize ML research</p>
            <p>✓ Secure authentication (passkeys or email)</p>
            <p>✓ No passwords needed - use your device unlock or email links</p>
          </div>
        </CardContent>
      </Card>

      {urlError && (
        <div className="max-w-md mx-auto mb-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{urlError}</AlertDescription>
          </Alert>
        </div>
      )}

      {mode === 'login' ? (
        <div className="space-y-4 max-w-md mx-auto">
          <LoginCard onSuccess={handleAuthSuccess} onError={handleAuthError} />

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 border-t border-muted"></div>
            <span className="text-sm text-muted-foreground">or</span>
            <div className="flex-1 border-t border-muted"></div>
          </div>

          <Button
            variant="outline"
            onClick={() => setMode('email')}
            className="w-full gap-2"
          >
            <Mail className="h-4 w-4" />
            Sign in with Email
          </Button>

          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <button
                onClick={() => setMode('register')}
                className="text-primary hover:underline font-medium"
              >
                Create one
              </button>
            </p>
          </div>
        </div>
      ) : mode === 'register' ? (
        <div className="space-y-4 max-w-md mx-auto">
          <RegisterForm onSuccess={handleAuthSuccess} onError={handleAuthError} />

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 border-t border-muted"></div>
            <span className="text-sm text-muted-foreground">or</span>
            <div className="flex-1 border-t border-muted"></div>
          </div>

          <Button
            variant="outline"
            onClick={() => setMode('email')}
            className="w-full gap-2"
          >
            <Mail className="h-4 w-4" />
            Sign up with Email
          </Button>

          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <button
                onClick={() => setMode('login')}
                className="text-primary hover:underline font-medium"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4 max-w-md mx-auto">
          <EmailAuth 
            onSuccess={handleAuthSuccess} 
            onError={handleAuthError}
            initialToken={urlToken || undefined}
          />

          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Prefer passkeys?{' '}
              <button
                onClick={() => {
                  setMode('login');
                  setUrlToken(null);
                }}
                className="text-primary hover:underline font-medium"
              >
                <Key className="inline h-3 w-3 mr-1" />
                Use Passkey
              </button>
            </p>
          </div>
        </div>
      )}
    </>
  );
}